import {useRouter} from 'next/dist/client/router';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import {useAccountManageWallet} from '../../api/hooks/useAccountManageWallet.hook';
import {useContextSsr} from '../../api/hooks/useContextSSr.hook';
import {useFooter} from '../../api/hooks/useFooter.hook';
import {useFullVariantByIdSsr} from '../../api/hooks/useFullVariantByIdSsr.hook';
import {useInstanceDetailPage} from '../../api/hooks/useInstanceDetail.hook';
import {useInstanceMetaByUrlSsr} from '../../api/hooks/useInstanceMetaByUrlSsr.hook';
import {useVariantDetailPage} from '../../api/hooks/useVariantDetailPage.hook';
import {VariantImage} from '../../api/models/variant-image';
import {VariantImageType} from '../../api/models/variant-image-type';
import Accordion from '../../components/_shared/accordion/accordion';
import Breadcrumbs from '../../components/_shared/breadcrumbs/breadcrumbs';
import GenericAttributeList from '../../components/_shared/generic-attribute-box/generic-attribute-box';
import {DefaultHeader} from '../../components/_shared/header/default-header/default-header';
import {HeaderFooterWrapper} from '../../components/_shared/header-footer-wrapper/header-footer-wrapper';
import {MetaTagsResolver} from '../../components/_shared/meta-tags-resolver/meta-tags-resolver';
import SalesBox from '../../components/_shared/sales-box/sales-box';
import {Spacer} from '../../components/_shared/spacer/spacer';
import {Typography} from '../../components/_shared/typography/typography';
import MoreInstancesBox from '../../components/instance-detail/instance-detail-more-instances-box/more-instances-box';
import {InstanceDetailStage} from '../../components/instance-detail/instance-detail-stage/instance-detail-stage';
import TokenHistoryBox
    from '../../components/instance-detail/instance-detail-token-history-box/instance-detail-token-history-box';
import {
    InstanceDetailUnlockables
} from '../../components/instance-detail/instance-detail-unlockables/instance-detail-unlockables';
import OriginatorList from '../../components/variant-detail/product-attributes/originator-list/originator-list';
import PublisherInfoBox from '../../components/variant-detail/publisher-info-box/publisher-info-box';
import LegalBox from '../../components/variant-detail/variant-attributes/legal-box/legal-box';
import ProductArtifactBox
    from '../../components/variant-detail/variant-attributes/product-artifact-box/product-artifact-box';
import SampleBox from '../../components/variant-detail/variant-attributes/sample-box/sample-box';
import UnlockablesBox from '../../components/variant-detail/variant-attributes/unlockables-box/unlockables-box';
import {
    VariantDetailStageMetaBoxAudioVideo
} from '../../components/variant-detail/variant-detail-stage/variant-detail-stage-meta-box/variant-detail-stage-meta-box-audio-video/variant-detail-stage-meta-box-audio-video';
import {
    fetchAttachments,
    fetchInstance,
    getEthereumPriceData,
    RootState,
    setAttachments,
    useAppSelector
} from '../../redux';
import {checkGeoblocking} from '../../redux/slices/user-slice';
import {SalesType} from '../../utils/_enums/sales-type';
import {TypographyVariantsEnum} from '../../utils/_enums/typeography-variants.enum';
import {generateUrlToDetailedPage} from '../../utils/_helper/generate-url-to-details-page-helper';
import useFilterAttachmentsSsr from '../../utils/_hooks/useFilterAttachmentsSsr.hook';
import styles from './instance-detail-page.module.scss';

export const InstanceDetailPage: React.FC & { dataHooks: any } = () => {
    const dispatch = useDispatch();
    const router = useRouter();
    const data = useInstanceDetailPage();
    const variantData = useVariantDetailPage();
    const variant = useFullVariantByIdSsr();
    const {host, locale} = useContextSsr();
    const protocol = host?.includes('creatokia') ? 'https://' : 'http://';
    const path = generateUrlToDetailedPage(
        variant?.productName ?? '',
        variant?.title ?? '',
        variant?.productId ?? '',
        variant?.id ?? -1,
        parseInt(router.query.instanceId as string)
    );
    const {
        jwt
    } = useAppSelector((state) => ({
        jwt: state.authentification.jwt
    }));

    const {userId, walletId, instance, attachments} = useAppSelector((state: RootState) => ({
        userId: state.userSlice.user?.id,
        walletId: state.blockchainSlice.walletId,
        instance: state.productSlice.instance,
        attachments: state.productSlice.attachments
    }));

    const covers = useFilterAttachmentsSsr(
        VariantImageType.COVER_ARTWORK,
        variant?.attachments
    );

    const instanceMeta = useInstanceMetaByUrlSsr();

    const instanceMetaAttributes = instanceMeta?.attributes?.map(attr => {
        return {
            label: attr.trait_type,
            value: attr.value
        }
    }) || {}

    useEffect(() => {
        dispatch(checkGeoblocking());
        dispatch(getEthereumPriceData());
    }, []);

    useEffect(() => {
        dispatch(fetchInstance(parseInt(router.query.instanceId as string)));
    }, [router.query.instanceId]);

    useEffect(() => {
        dispatch(fetchAttachments(variant.id));
        return () => {
            dispatch(setAttachments());
        };
    }, [jwt]);

    const [openModal, setModal] = useState(false);
    const [sample, setSample] = useState<VariantImage | undefined>(undefined);
    const handleOpenSampleModal = (sample: VariantImage) => {
        setSample(sample);
        setModal(true);
    }

    const salesBoxRef = React.createRef();

    const scrollIntoView = () => salesBoxRef.current.scrollIntoView({behavior: 'smooth'});

    let unlockableBox: JSX.Element = <UnlockablesBox/>
    let isOwner = false;

    if (!!instance && (!!userId && userId === instance.ownerId || walletId === instance.ownerWallet)) {
        unlockableBox = undefined;
        isOwner = true;
    }

    return (
        <div>
            <MetaTagsResolver
                tags={{
                    metaOgTitle: variant?.title,
                    metaTagKeywords: 'Creatokia, NFT, Crypto',
                    metaTagTitle: variant?.title,
                    metaTagDescription: variant?.variantDescriptionSEO,
                    metaTagCanonical: host,
                    metaOgDescription: variant?.variantDescriptionShort,
                    metaOgUrl: `${protocol}${host}/${
                        locale ?? 'en'
                    }${path}`,
                    metaTwitterSite: `${protocol}${host}/${
                        locale ?? 'en'
                    }${path}`,
                    metaOgSiteName: 'Creatokia',
                    MetaOgImage: {
                        url: covers?.[0]?.urls?.original,
                        id: covers?.[0]?.id ?? -1,
                        alternativeText: variant?.title,
                        name: variant?.title,
                        width: 0,
                        height: 0,
                        caption: ''
                    },
                    metaNoFollow: true,
                    metaNoIndex: true
                }}
                languageAlternates={[
                    {
                        hrefLang: 'en',
                        href: `${protocol}${host}/en${path}`
                    },
                    {
                        hrefLang: 'de',
                        href: `${protocol}${host}/de${path}`
                    }
                ]}
                locale={locale?.toUpperCase()}
                localeAlternate={locale === 'de' ? 'EN' : 'DE'}
            />
            <HeaderFooterWrapper
                header={<DefaultHeader availableLanguages={data?.localizations}/>}
                stage={<InstanceDetailStage onButtonClick={scrollIntoView}/>}
            >
                <div className={styles['instance-detail-content-container']}>
                    <Breadcrumbs breadcrumbs={[
                        {name: data?.breadcrumbHome, href: '/'},
                        {name: data?.breadcrumbDigitalOriginals, href: '/digital-originals'},
                        {
                            name: variant.title, href: generateUrlToDetailedPage(
                                variant.productName,
                                variant.title,
                                variant.productId,
                                variant.id
                            )
                        },
                        {name: (router.query.instanceId as string), href: ''}
                    ]}/>
                    <div className={styles['accordion-wrapper']}>
                        <div className={styles['sales-box-container']}>
                            <Accordion
                                label={variantData.variantDescription.headlineContent}
                                onlyMobil={true}
                                initialOpen={false}
                                reverseContent={true}
                            >
                                <div className={styles['sales-box']} ref={salesBoxRef}>
                                    <SalesBox salesType={SalesType.FIXED_PRICE}/>
                                </div>

                                <Typography
                                    variant={TypographyVariantsEnum.HEADING3_BOLD}
                                    element={'h3'}
                                >
                                    {variantData?.variantDescription.headlineContent}
                                </Typography>

                                <Typography className={styles['description']}>
                                    {instanceMeta.description}
                                </Typography>

                            </Accordion>
                        </div>

                        <TokenHistoryBox/>

                        <div className={styles['product-details']}>
                            <Accordion
                                label={variantData?.variantAttributes.headlineContent}
                                onlyMobil={true}
                                initialOpen={true}
                            >
                                <Typography
                                    variant={TypographyVariantsEnum.HEADING3_BOLD}
                                    element={'h3'}
                                >
                                    {variantData?.variantAttributes.headlineContent}
                                </Typography>
                                <Spacer height="40px"/>
                                <div className={styles['instance-info-container']}>
                                    <div>
                                        <ProductArtifactBox isOwner={isOwner} instanceId={instance?.id}/>
                                        <SampleBox onClick={handleOpenSampleModal}/>
                                        <LegalBox/>
                                    </div>
                                    <div>
                                        <GenericAttributeList
                                            headline={variantData?.additionalAttributes.headlineContent}
                                            attributes={instanceMetaAttributes}
                                        />
                                    </div>
                                </div>
                            </Accordion>
                        </div>
                        <InstanceDetailUnlockables instanceCoverArtUrl={instanceMeta.image}/>
                    </div>

                    {unlockableBox}

                    <div className={styles['originatorList']}>
                        <OriginatorList/>
                    </div>
                </div>

                <PublisherInfoBox className={styles['publisher-desktop']}/>

                <MoreInstancesBox/>

                {openModal && (
                    <VariantDetailStageMetaBoxAudioVideo
                        type={sample?.contentContentType ?? ''}
                        title={sample?.name ?? ''}
                        onClose={() => setModal(false)}
                        url={sample?.urls?.original ?? ''}
                        id={variant?.id}
                    />
                )}
            </HeaderFooterWrapper>
        </div>
    );
};

InstanceDetailPage.dataHooks = [
    ...HeaderFooterWrapper.dataHooks,
    useInstanceDetailPage,
    useVariantDetailPage,
    useFooter,
    useAccountManageWallet,
    ...MetaTagsResolver.dataHooks,
    useFullVariantByIdSsr,
    useContextSsr
];
